import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { objectToQueryString, queryStringToObject } from "../utils/query";
import usePromise from "../utils/hooks/usePromise";
import { services } from "../services";
import {
  District,
  State,
  VLE,
} from "@digital-girls-hub/shared-resources/models/db";
import DataTable from "react-data-table-component";
import moment from "moment";
import { downloadFileFromURL } from "../utils/file";
import AdminLayout from "../layouts/AdminLayout";
import { useAuth } from "../utils/hooks/useAuth";
import PageTabs from "../components/PageTabs";
import { ADMIN_ROLE, isStateAdmin } from "../utils/roles";
import { UpdateVLEWhitelistContent } from "./UpdateVLEWhitelist";
import { DelistVLEWhitelistContent } from "./DelistVLEWhitelist";
import { OnboardingLimitSettingsContent } from "./AdminSettingsSections";

type VLEDataRecord = {
  id: bigint;
  name: string;
  email: string;
  cscId: string;
  stateId: bigint;
  placementPartnerId: bigint | null;
  selfPlacedCount: number;
  placedByPPCount: number;
  totalPlacedCount: number;
  createdAt: string;
  updatedAt: string;
  districtId: bigint;
  userOnboardingCount: number;
  userConfirmedOnboardingCount: number;
  userCourseCompletionCount: number;
  stateName: string;
  districtName: string;
  districtCode: string;
  placementPartnerName: string | null;
};

type VLETab = "reports" | "whitelist" | "delist" | "limits";

export function VLEStatsContent() {
  const navigate = useNavigate();
  const { admin, role } = useAuth();
  const stateAdmin = isStateAdmin(role);
  const isSuperAdmin = role === ADMIN_ROLE.SUPER_ADMIN;
  const [activeTab, setActiveTab] = useState<VLETab>("reports");

  /*




  Parsing and setting stats parameters




  */
  const searchQuery: {
    params: { vleCscId?: string; stateId?: number; districtId?: number };
    paginationParams: {
      pageIndex: number;
      pageSize: number;
      orderBy: { fieldName: keyof VLE; dir: "asc" | "desc" }[];
    };
  } = useMemo(() => {
    return {
      params: {},
      paginationParams: {
        pageSize: 10,
        pageIndex: 0,
        orderBy: [],
      },
      ...queryStringToObject(window.location.search.slice(1)),
    };
  }, [window.location.search]);

  const setSearchQuery = useCallback(
    (newSearchQuery: typeof searchQuery) => {
      const queryString = objectToQueryString(newSearchQuery);
      if (queryString !== window.location.search.slice(1)) {
        navigate({ search: queryString });
      } else runSearch();
    },
    [navigate],
  );

  /*




  Fetching states and district data




  */
  const {
    result: states,
    error: fetchStatesErr,
    loading: loadingStates,
  } = usePromise(
    {
      fn: () =>
        services.requestSvc
          .get<State[]>("/states", { withCredentials: true })
          .then((e) => {
            if (admin?.stateId)
              return e.filter((e) => BigInt(e.id) === admin.stateId);
            return e;
          }),
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [admin?.stateId],
  );

  const {
    result: districts,
    error: fetchDistrictsErr,
    loading: loadingDistricts,
  } = usePromise(
    {
      fn: async () => {
        if (searchQuery.params.stateId) {
          return services.requestSvc.get<District[]>(
            "/districts-by-state-id?stateId=" + searchQuery.params.stateId,
            { withCredentials: true },
          );
        } else return [];
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [searchQuery.params.stateId],
  );

  /*




  Fetching records




  */
  const {
    run: runSearch,
    result: vleList,
    loading: fetchingVLEList,
    error: fetchVLEError,
  } = usePromise(
    {
      fn: async () =>
        services.requestSvc.post<{
          data: VLEDataRecord[];
          count: number;
        }>("/get-vles-by-params-paginated", searchQuery, {
          withCredentials: true,
        }),
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [searchQuery],
  );

  /*




  Download Reports




  */
  const {
    run: downloadCSV,
    loading: downloadingCsvReport,
    error: downloadCSVError,
  } = usePromise(
    {
      fn: async () => {
        const { csvReportDownloadLink, csvReportFilename } =
          await services.requestSvc.post<{
            csvReportDownloadLink: string;
            csvReportFilename: string;
          }>("/download-vle-report", searchQuery.params, {
            withCredentials: true,
          });

        downloadFileFromURL(csvReportDownloadLink, csvReportFilename);
      },
    },
    [searchQuery],
  );

  /*




  Misc




  */
  const [transientCscId, setTransientCscId] = useState("");

  const setSearchParams = useCallback(
    (field: keyof typeof searchQuery.params) =>
      (value: (typeof searchQuery.params)[typeof field]) => {
        Object.assign(searchQuery.params, { [field]: value });

        const district = districts?.find(
          (d) => Number(d.id) === Number(searchQuery.params.districtId),
        );

        if (
          !district ||
          Number(district.stateId) !== Number(searchQuery.params.stateId)
        ) {
          searchQuery.params.districtId = undefined;
        }

        navigate({ search: objectToQueryString(searchQuery.params) });
        setSearchQuery({ ...searchQuery });
      },
    [searchQuery, setSearchQuery, districts, navigate],
  );

  useEffect(() => {
    if (states?.length === 1) {
      setSearchParams("stateId")(Number(states[0].id));
    }
  }, [states, searchQuery, setSearchParams]);

  return (
    <div className="max-w-6xl flex flex-col gap-6 w-full">
      <div className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm">
        <PageTabs
          tabs={[
            { id: "reports", label: "Reports" },
            ...(isSuperAdmin
              ? [
                  { id: "whitelist" as VLETab, label: "VLE Whitelist" },
                  { id: "delist" as VLETab, label: "VLE Delist" },
                  { id: "limits" as VLETab, label: "Onboarding Limits" },
                ]
              : []),
          ]}
          activeTab={activeTab}
          onChange={(tabId) => setActiveTab(tabId as VLETab)}
        />
      </div>

      {activeTab === "reports" && (
        <div className="flex flex-col w-full max-w-[1600px] items-start">
          <div className="flex flex-col justify-center gap-y-2">
            <div className="flex flex-col lg:flex-row gap-x-2 gap-y-2">
              <input
                type="text"
                className="input-primary w-full lg:w-1/2"
                onChange={(e) => setTransientCscId(e.target.value)}
                onKeyDownCapture={(e) => {
                  if (e.key === "Enter")
                    setSearchParams("vleCscId")(transientCscId);
                }}
                placeholder="Enter VLE's CSC ID"
                value={transientCscId}
              />
              <div className="flex w-full gap-x-2 lg:w-1/2">
                <select
                  onChange={(e) =>
                    setSearchParams("stateId")(e.target.value || undefined)
                  }
                  className="input-primary w-1/2"
                  value={searchQuery.params.stateId}
                  disabled={stateAdmin}
                >
                  {!stateAdmin && <option value="">All States</option>}
                  {states?.map((s) => (
                    <option value={Number(s.id)} key={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>

                <select
                  onChange={(e) =>
                    setSearchParams("districtId")(e.target.value || undefined)
                  }
                  className="input-primary w-1/2"
                  value={searchQuery.params.districtId}
                >
                  <option value="">All Districts</option>
                  {districts?.map((d) => (
                    <option value={Number(d.id)} key={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              className="btn-primary"
              onClick={() => {
                setSearchParams("vleCscId")(transientCscId);
              }}
            >
              Search
            </button>
            <button className="btn-secondary" onClick={downloadCSV}>
              Download CSV Report
            </button>
          </div>
          <DataTable<VLEDataRecord>
            columns={[
              {
                id: "vle-id",
                name: "VLE ID",
                selector: (row) => row.cscId,
                width: "18rem",
              },
              {
                id: "vle-name",
                name: "VLE Name",
                selector: (row) => row.name,
                width: "16rem",
              },
              {
                name: "State",
                selector: (row) => row.stateName,
              },
              {
                name: "District",
                selector: (row) => row.districtName || "Unknown",
              },
              {
                name: "Placement Partner",
                selector: (row) => row.placementPartnerName || "Unassigned",
                width: "18rem",
              },
              {
                name: "Self Placed",
                selector: (row) => row.selfPlacedCount || 0,
                width: "10rem",
              },
              {
                name: "Placed by PP",
                selector: (row) => row.placedByPPCount || 0,
                width: "10rem",
              },
              {
                name: "Total Placed",
                selector: (row) => row.totalPlacedCount || 0,
                width: "10rem",
              },
              {
                name: "Onboarding Count",
                selector: (row) => row.userOnboardingCount,
                width: "19rem",
              },
              {
                name: "Course Completion Count",
                selector: (row) => row.userCourseCompletionCount,
              },
              {
                name: "Activated On",
                selector: (row) =>
                  moment(row.createdAt).format("Y MMM DD, HH:mm:SS"),
              },
            ]}
            data={vleList?.data || []}
            pagination
            paginationServer
            paginationPerPage={searchQuery.paginationParams.pageSize}
            paginationRowsPerPageOptions={[5, 10, 20, 50]}
            paginationTotalRows={vleList?.count}
            onChangePage={(pageNumber) => {
              setSearchQuery({
                ...searchQuery,
                paginationParams: {
                  ...searchQuery.paginationParams,
                  pageIndex: pageNumber - 1,
                },
              });
            }}
            onChangeRowsPerPage={(currentRowsPerPage) => {
              setSearchQuery({
                ...searchQuery,
                paginationParams: {
                  ...searchQuery.paginationParams,
                  pageIndex: 0,
                  pageSize: currentRowsPerPage,
                },
              });
            }}
            className="w-full mt-10 vle-report-table"
          />
        </div>
      )}

      {activeTab === "whitelist" && isSuperAdmin && (
        <UpdateVLEWhitelistContent />
      )}
      {activeTab === "delist" && isSuperAdmin && <DelistVLEWhitelistContent />}

      {activeTab === "limits" && isSuperAdmin && (
        <OnboardingLimitSettingsContent />
      )}
    </div>
  );
}

export default function VLEStatsPage() {
  return (
    <AdminLayout title="VLE">
      <VLEStatsContent />
    </AdminLayout>
  );
}
